// @ts-nocheck
import util from '../../../lib/util';
import store from '../../../store/store';
import {isCasePayer} from '../../../Case/caseParties';
import PaymentBankAccount from '../types/PaymentBankAccount';
import PaymentCreditCard from '../types/PaymentCreditCard';
import paymentApi, {LAWPAY_READ_ONLY} from '../paymentApi';
import lawpayLogo from './LawPay-Logo.png';
import {isPortalUser} from "../../../lib/picos/currentLogin";
import Contact from "../../../entities/contact";
import Lead from "../../../entities/lead";
import Matter from "../../../entities/matter";
import type BasePaymentMethod from "../types/BasePaymentMethod";
import type {PaymentAddress} from "../types/BasePaymentMethod";
import Address from "../../../entities/address";
import type {LawpayContact} from "../../../@types/lawpay";
import type {uuid} from "../../../@types/entity";

export type LawpayPaymentMeta = Partial<{
    amount: number;
    method: Partial<{
        number: string,
        accountType: any,
        cardType: string,
        type: string,
        fingerprint: string
    }>,
    authorization_code: string,
    id: string,
    type: string,
    chargeDate: Date,
    voidOrRefundDate: Date,
    status: string,
    amount_refunded: number,
    fingerprint: string
}>

const NO_ACCOUNTS: any[] = [];

/**
 * Read-only delegate for firms whose stored payment processor is the historical 'lawpay'.
 *
 * LawPay is closed (OWA-826) and the Rust server has no /lawpayauth route (OWA-1573), so nothing here
 * calls the network: reads return empty results and writes refuse with a clear error. The delegate
 * exists so getDelegate('lawpay') never throws for a historical firm.
 */
class LawPay {

    /**
     * The LawPay icon.
     *
     * @type {*}
     */
    static getIcon(): string {
        return lawpayLogo;
    }

    /**
     * The LawPay brand name.
     *
     * @returns {string}
     */
    static getName(): string {
        return 'LawPay';
    }


    /**
     * Is the firm connected to LawPay (the stored processor is 'lawpay')?
     *
     * @returns {boolean}
     */
    static isConnected(): boolean {
        return paymentApi.isConnected('lawpay');
    }

    /**
     * LawPay accounts are not available: the connection is closed. Every call returns the same
     * array, because the Banking page re-fetches whenever the accounts it holds change and a fresh
     * array each time would loop forever.
     */
    static async getAccounts(): Promise<any[]> {
        return NO_ACCOUNTS;
    }

    static async getContacts(): Promise<LawpayContact[]> {
        return [];
    }

    static async getPaymentMethods(contact: Contact): Promise<(PaymentCreditCard | PaymentBankAccount)[]> {
        return [];
    }

    static async getTransactions(startDate?: Date, endDate?: Date): Promise<any[]> {
        return [];
    }

    /** Nothing to sync or delete: there is no LawPay service to reach. */
    static async syncContacts(contacts: Contact[]): Promise<void> {}

    static async deleteContact(processorContactId: string): Promise<void> {}

    /** Every write refuses: LawPay is historical and read-only. */
    static refuseWrite(): never {
        throw new Error(LAWPAY_READ_ONLY);
    }

    static async updateContact(contact: Contact): Promise<string> { return LawPay.refuseWrite(); }
    static async createPaymentMethod(contact: Contact, paymentMethod: BasePaymentMethod): Promise<any> { return LawPay.refuseWrite(); }
    static async updatePaymentMethod(contact: Contact, paymentMethod: BasePaymentMethod): Promise<any> { return LawPay.refuseWrite(); }
    static async deletePaymentMethod(contact: Contact, paymentMethod: BasePaymentMethod): Promise<any> { return LawPay.refuseWrite(); }
    static async createPayment(paymentMethodId: string, amount: number, accountId: string, transaction?: string): Promise<any> { return LawPay.refuseWrite(); }
    static async refundTransaction(transactionId: string, amount: number): Promise<any> { return LawPay.refuseWrite(); }
    static async voidTransaction(transactionId: string): Promise<any> { return LawPay.refuseWrite(); }
    static async getTransaction(transactionId: string): Promise<any> { return LawPay.refuseWrite(); }

    /**
     * Only relevant for Payrix.
     *
     * @param {uuid} txnId
     * @param {string} description
     */
    static async updateTxn(txnId: uuid, description: string): Promise<void> {
        throw new Error('Not implemented for LawPay');
    }

    /**
     * Is this contact eligible to have payments?
     *
     * Note that this seems to differ between LawPay and Payrix, so this should be at the processor level.
     *
     * @param {Contact} contact
     * @param {Lead | Matter} unsavedEntity
     * @returns {boolean} true if payments available
     */
    static paymentEligible(contact: Contact, unsavedEntity?: Lead | Matter): boolean {
        if (!contact) return false;
        if (!paymentApi.usesPayments()) return false;

        // A client or benefactor of a Case or Lead (OWA-1341: caseParties, never the lookup key)
        const bus = contact.isCompany;
        const basics = ((bus && contact.last) || (!bus && (contact.first && contact.last))) && contact.status !== 'D' && isCasePayer(contact.id, unsavedEntity);
        const hasEmails = !!store.getState().resources.find(r => r.entityId === contact.id && r.type === 'email' && r.isPrimary && r.canContact);
        const hasAddresses = !!store.getState().addresses.find(a => a.entityId === contact.id && a.isPrimary && a.canContact);

        return (basics && hasEmails && hasAddresses);
    };




    /**
     * Format Outlaw address for LawPay.
     *
     * @param {Address} address
     * @returns {PaymentAddress}
     */
    static formatAddress(address: Address): PaymentAddress & {type: string} {
        return {
            type: address.label,
            line1: address.street,
            line2: address.unit,
            city: address.city,
            state_or_province: address.state,
            postal_code: address.zip,
            country_code: address.country
        }
    }

    /**
     * Format in LawPay's preferred format.
     *
     * @param {BasePaymentMethod} paymentMethod
     * @returns {any}
     */
    static formatPaymentMethod(paymentMethod: any): BasePaymentMethod {
        if (paymentMethod.paymentType === 'card') {
            const {
                id, paymentType, number, cardType, cvv, exp_month, exp_year, name, nickname, created, modified,
                fingerprint, status, email, address1, city, state, postal_code, phone, paymentToken
            } = paymentMethod;

            return new PaymentCreditCard({
                id,
                paymentType,
                number,
                cardType,
                cvv,
                exp_month,
                exp_year,
                name,
                nickname,
                created,
                modified,
                fingerprint,
                status,
                email,
                address: {address1, city, state, postal_code},
                phone: phone ? Number.parseInt(`${phone}`.replaceAll(/\D/g, '')) : null,
                paymentToken
            });
        }
        else {
            const {
                id, paymentType, account_type, account_number, account_holder_type, routing_number, name, given_name,
                surname, nickname, email, address1, city, state, postal_code, phone, paymentToken
            } = paymentMethod as PaymentBankAccount;

            if (account_holder_type === 'individual')
                return new PaymentBankAccount({
                    id,
                    paymentType,
                    account_number,
                    account_type: account_type.toUpperCase(),
                    account_holder_type: account_holder_type.toLowerCase(),
                    routing_number,
                    given_name,
                    surname,
                    nickname,
                    email,
                    address: {address1, city, state, postal_code},
                    phone: phone ? Number.parseInt(`${phone}`.replaceAll(/\D/g, '')) : null,
                    paymentToken
                });
            else
                return new PaymentBankAccount({
                    id,
                    paymentType,
                    account_number,
                    account_type: account_type.toUpperCase(),
                    account_holder_type: account_holder_type.toLowerCase(),
                    routing_number,
                    name,
                    given_name,
                    surname,
                    nickname,
                    email,
                    address: {
                        address1,
                        city,
                        state,
                        postal_code
                    },
                    phone: phone ? Number.parseInt(`${phone}`.replaceAll(/\D/g, '')) : null,
                    paymentToken
                })
        }
    }


    /**
     * Parse out fields we care about from a payment/void/refund, specifically:
     * - authorization_code: for display to the user
     * - id: the ID of the transaction, for later lookup
     * - status: e.g. VOIDED, AUTHORIZED, PENDING, COMPLETED, ACTIVE
     * - type: e.g. CHARGE, REFUND, CREDIT (note that void returns CHARGE)
     * - amount: the amount of the charge
     * - amount_refunded: the amount refunded (if a refund)
     * - chargeDate: the date the payment method was initially charged
     * - voidOrRefundDate: the date the payment was (last) voided or refunded
     *
     * @param {any} meta
     * @returns {LawpayPaymentMeta}
     */
    static parsePaymentMeta(meta: any): LawpayPaymentMeta {
        const {id, type, status, authorization_code, amount, amount_refunded, created, modified, method} = meta;
        return {
            id,
            type,
            status,
            authorization_code,
            amount: amount / 100,
            amount_refunded: amount_refunded / 100,
            chargeDate: new Date(created),
            voidOrRefundDate: new Date(modified),
            method: {
                type: method.type,
                number: (method.number || method.account_number)?.replaceAll('*', ''),
                cardType: method.type === 'card' ? util.snakeToText(method.card_type) : null,
                accountType: method.type === 'bank' ? method.account_type.toLowerCase() : null,
                fingerprint: method.fingerprint
            }
        };
    }

    /**
     * Get the current status of a transaction.
     *
     * This will return an array of status flags.  Specifically:
     *
     * - NONE: no processor action is associated with this transaction.
     * - INCOMPLETE:  the transaction is in progress, and can be voided.
     * - COMPLETED: the transaction has been settled, and can be refunded but not voided.
     * - VOID: the transaction has been voided.  This is immediate, so once voided nothing can be done.
     * - REFUND: the transaction is being/has been refunded.  Check for INCOMPLETE vs COMPLETED.
     * - CHARGE: the transaction is being/has been processed as a charge.  Check for INCOMPLETE vs COMPLETED.
     *
     * Valid combinations are:
     * - NONE
     * - CHARGE/INCOMPLETE
     * - CHARGE/COMPLETE
     * - VOID/COMPLETE
     * - REFUND/INCOMPLETE
     * - REFUND/COMPLETE
     *
     * @param {object} tr
     * @returns {flags: Array.<'CHARGE'|'REFUND'|'INCOMPLETE'|'COMPLETE'|'VOID'>, message: string} - an array of flags indicating current status.
     */
    static getCurrentStatus(tr: Transaction) {
        const meta = tr.processorPaymentMeta;
        if (!meta) return {flags: [], message: 'No status available.'}
        
        const {status, type, amount} = meta;

        const amt = util.formatCurrency(Number.parseFloat(amount), true);

        if (type === 'CHARGE') {
            if (status === 'AUTHORIZED') return {
                flags: ['CHARGE', 'INCOMPLETE'],
                message: `Payment of ${amt} has been submitted${isPortalUser() ? '' : ', waiting for funds to settle'}.`
            };
            else if (status === 'COMPLETED') return {
                flags: ['CHARGE', 'COMPLETE'],
                message: `Payment of ${amt} has settled.`
            };
            else if (status === 'VOIDED') return {
                flags: ['CHARGE', 'VOID'],
                message: `Payment of ${amt} has been voided.`
            };
            else throw Error(`Unknown status received: '${status}'`);
        }
        else if (type === 'REFUND') {
            if (status === 'AUTHORIZED') return {
                flags: ['REFUND', 'INCOMPLETE'],
                message: `Refund of ${amt} has been submitted${isPortalUser() ? '' : ', waiting for funds to settle'}.`
            };
            else if (status === 'COMPLETED' || status === 'REFUND') return {
                flags: ['REFUND', 'COMPLETE'],
                message: `Refund of ${amt} has been completed.`
            };
            else if (status === 'VOIDED') return {
                flags: ['REFUND', 'VOID'],
                message: `Refund of ${amt} has been voided.`
            };
            else throw Error(`Unknown type received: '${type}'`);
        }
    }

    /**
     * Get the completion date from the metadata.
     *
     * @param {object} meta
     * @return {Date|null}
     */
    static getCompletionDate(meta) {
        return meta?.completion_date;
    }
}

export default LawPay;
